import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useAdyen } from './AdyenProvider';
import classNames from 'classnames';
import { LoadingIcon } from 'nexticons/outline';

const AdyenDropinPayment = forwardRef(function AdyenDropinPayment(
  {
    onSubmit,
    onAdditionalDetails,
    onBrand,
    onClick,
    isCardPaymentAvailable,
    shouldHide,
    ...restProps
  },
  ref
) {
  const { checkout, mountComponent, isLoading } = useAdyen();
  const containerId = 'dropin-container';
  const dropinRef = useRef(null);
  const lastCheckoutRef = useRef(null);
  const mounted = useRef(false);

  const unMountDropin = () => {
    if (dropinRef.current) {
      try {
        dropinRef.current.unmount();
      } catch (err) {
        console.warn('Unmount failed:', err);
      }
    }
  };

  const resetDropin = async (toPay) => {
    if (checkout) {
      unMountDropin();
      dropinRef.current = await mountComponent({
        paymentMethod: 'dropin',
        containerId,
        toPay,
        onSubmit,
        onAdditionalDetails,
        onBrand,
        ...restProps,
      });
    }
  };

  useImperativeHandle(ref, () => ({
    resetDropin,
  }));

  useEffect(() => {
    const container = document.getElementById(containerId);

    if (!checkout || isLoading || !container) return;

    const checkoutChanged = lastCheckoutRef.current !== checkout;

    if (checkoutChanged) mounted.current = false;

    resetDropin();
    lastCheckoutRef.current = checkout;
    mounted.current = true;

    return () => {
      unMountDropin();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout, isLoading]);

  return (
    <div
      className={classNames({
        'relative mt-4 min-h-[200px]': isCardPaymentAvailable,
        'hidden-adyen': !isCardPaymentAvailable || shouldHide,
      })}
    >
      <div
        className={classNames(
          'absolute inset-0 flex flex-col items-center justify-center z-10 bg-white bg-opacity-80 rounded transition-opacity duration-300',
          {
            'opacity-0 pointer-events-none': checkout && !isLoading,
          }
        )}
      >
        <LoadingIcon strokeWidth={3} width={40} />
        <p className='mt-3 text-base text-black'>Loading payment methods...</p>
      </div>

      <div
        id={containerId}
        className={classNames('transition-opacity duration-300', {
          'opacity-0 pointer-events-none': !checkout || isLoading,
        })}
        onClick={onClick}
      ></div>
    </div>
  );
});

export default AdyenDropinPayment;
